import { setPath } from "../logic/paths";
import {
  computeRiskProfile,
  RiskQuestion,
  setRiskAnswer,
} from "../logic/riskProfiler";
import {
  Errors,
  LoadedSnapshot,
  MarriageNameMatchesId,
  UploadEntry,
  WizardState,
} from "./types";

export const initialState: WizardState = {
  phase: "loading",
  tier: null,
  formVariant: "",
  reviewStatus: "none",
  stepIndex: 0,
  reachedIndex: 0,
  draft: { user: {}, kyc: {}, riskQuestionnaire: {}, accountDetails: {} },
  originalKyc: {},
  errors: {},
  uploads: {},
  marriageNameMatchesId: "",
  risk: { result: "", dirty: false },
  confirmed: false,
  sameAsPresentAddress: false,
  serverError: "",
  loadError: "",
};

export type WizardAction =
  | { type: "RETRY_LOAD" }
  | { type: "LOAD_SUCCESS"; snapshot: LoadedSnapshot }
  | { type: "LOAD_ERROR"; message: string }
  | { type: "SET_USER_FIELD"; path: string; value: unknown }
  | { type: "SET_KYC_FIELD"; path: string; value: unknown }
  | { type: "SET_ACCOUNT_FIELD"; path: string; value: unknown }
  | { type: "SET_RISK_ANSWER"; question: RiskQuestion; selected: number }
  | { type: "SET_CONFIRMED"; value: boolean }
  | { type: "SET_SAME_AS_PRESENT"; value: boolean }
  | { type: "SET_MARRIAGE_NAME_MATCHES_ID"; value: MarriageNameMatchesId }
  | { type: "SET_UPLOAD"; image: string; entry: UploadEntry }
  | { type: "REMOVE_UPLOAD"; image: string }
  | { type: "GO_TO_STEP"; index: number }
  | { type: "SET_ERRORS"; errors: Errors }
  | { type: "CLEAR_FIELD_ERROR"; path: string }
  | { type: "SUBMIT_START" }
  | { type: "SUBMIT_SUCCESS" }
  | { type: "SUBMIT_PENDING" }
  | { type: "SUBMIT_ERROR"; message: string };

function omit(errors: Errors, key: string): Errors {
  if (!(key in errors)) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
}

export function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "RETRY_LOAD":
      return { ...state, phase: "loading", loadError: "" };

    case "LOAD_SUCCESS": {
      const s = action.snapshot;
      return {
        ...state,
        phase: s.reviewStatus === "pending" ? "pending" : "editing",
        tier: s.tier,
        formVariant: s.formVariant,
        reviewStatus: s.reviewStatus,
        draft: s.draft,
        originalKyc: s.originalKyc,
        risk: { result: s.riskResult, dirty: false },
        sameAsPresentAddress: s.sameAsPresentAddress,
        marriageNameMatchesId: s.marriageNameMatchesId,
      };
    }

    case "LOAD_ERROR":
      return { ...state, phase: "error", loadError: action.message };

    case "SET_USER_FIELD":
      return {
        ...state,
        draft: {
          ...state.draft,
          user: setPath(state.draft.user, action.path, action.value),
        },
        errors: omit(state.errors, `user.${action.path}`),
      };

    case "SET_KYC_FIELD": {
      let kyc = setPath(state.draft.kyc, action.path, action.value);
      let uploads = state.uploads;
      let marriageNameMatchesId = state.marriageNameMatchesId;
      let errors = omit(state.errors, action.path);
      // When permanent mirrors current, keep them in lockstep as current edits.
      if (state.sameAsPresentAddress && action.path.startsWith("currentAddress.")) {
        const permanentPath = action.path.replace(
          "currentAddress.",
          "permanentAddress."
        );
        kyc = setPath(kyc, permanentPath, action.value);
      }
      if (
        action.path === "civil.civilStatus" &&
        String(action.value || "").toLowerCase() !== "married"
      ) {
        uploads = { ...uploads };
        delete uploads.marriageCert;
        marriageNameMatchesId = "";
        errors = omit(omit(errors, "marriageNameMatchesId"), "marriageCert");
      }
      return {
        ...state,
        draft: { ...state.draft, kyc },
        uploads,
        marriageNameMatchesId,
        errors,
      };
    }

    case "SET_ACCOUNT_FIELD":
      return {
        ...state,
        draft: {
          ...state.draft,
          accountDetails: setPath(
            state.draft.accountDetails,
            action.path,
            action.value
          ),
        },
        errors: omit(state.errors, action.path),
      };

    case "SET_RISK_ANSWER": {
      const riskQuestionnaire = setRiskAnswer(
        state.draft.riskQuestionnaire,
        action.question,
        action.selected
      );
      return {
        ...state,
        draft: { ...state.draft, riskQuestionnaire },
        risk: { result: computeRiskProfile(riskQuestionnaire), dirty: true },
        errors: omit(state.errors, "riskQuestionnaire"),
      };
    }

    case "SET_CONFIRMED":
      return {
        ...state,
        confirmed: action.value,
        errors: action.value ? omit(state.errors, "confirmed") : state.errors,
      };

    case "SET_SAME_AS_PRESENT": {
      if (!action.value) return { ...state, sameAsPresentAddress: false };
      // Mirror the current address into permanent and drop stale permanent errors.
      const kyc = setPath(state.draft.kyc, "permanentAddress", {
        ...(state.draft.kyc.currentAddress || {}),
      });
      const errors: Errors = {};
      Object.keys(state.errors).forEach((key) => {
        if (!key.startsWith("permanentAddress.")) errors[key] = state.errors[key];
      });
      return {
        ...state,
        sameAsPresentAddress: true,
        draft: { ...state.draft, kyc },
        errors,
      };
    }

    case "SET_MARRIAGE_NAME_MATCHES_ID": {
      let uploads = state.uploads;
      let errors = omit(state.errors, "marriageNameMatchesId");
      if (action.value !== "no") {
        uploads = { ...uploads };
        delete uploads.marriageCert;
        errors = omit(errors, "marriageCert");
      }
      return {
        ...state,
        marriageNameMatchesId: action.value,
        uploads,
        errors,
      };
    }

    case "SET_UPLOAD":
      return {
        ...state,
        uploads: { ...state.uploads, [action.image]: action.entry },
        errors:
          action.entry.status === "uploaded"
            ? omit(state.errors, action.image)
            : state.errors,
      };

    case "REMOVE_UPLOAD": {
      const uploads = { ...state.uploads };
      delete uploads[action.image];
      return { ...state, uploads };
    }

    case "GO_TO_STEP":
      return {
        ...state,
        stepIndex: action.index,
        reachedIndex: Math.max(state.reachedIndex, action.index),
        errors: {},
        serverError: "",
      };

    case "SET_ERRORS":
      return { ...state, errors: action.errors };

    case "CLEAR_FIELD_ERROR":
      return { ...state, errors: omit(state.errors, action.path) };

    case "SUBMIT_START":
      return { ...state, phase: "submitting", serverError: "" };

    case "SUBMIT_SUCCESS":
      return { ...state, phase: "submitted" };

    case "SUBMIT_PENDING":
      return { ...state, phase: "pending", reviewStatus: "pending" };

    case "SUBMIT_ERROR":
      return { ...state, phase: "editing", serverError: action.message };

    default:
      return state;
  }
}
